/* eslint-disable max-len */
import { takeEvery, all, call, put } from 'redux-saga/effects';
import * as types from './weaveActionTypes';
import * as actions from './weaveActions';
import * as endpoints from '../../services/endpoints';
import * as alert from '../alert/alertActions';
import { errorHandler } from '../../helpers/errorHandler';

const flattenWeaveFactorRows = (weaveFactorRowsByThreadType) => {
  if (!weaveFactorRowsByThreadType) return [];

  const threadType1Rows = Array.isArray(weaveFactorRowsByThreadType[1]) ? weaveFactorRowsByThreadType[1] : [];
  const threadType2Rows = Array.isArray(weaveFactorRowsByThreadType[2]) ? weaveFactorRowsByThreadType[2] : [];

  return [...threadType1Rows, ...threadType2Rows]
    .map(row => ({
      weaveFactorId: row?.weaveFactorId || row?.WeaveFactorId || '',
      threadType: String(row?.threadType ?? row?.ThreadType ?? '').trim(),
      firstColon: String(row?.firstColon ?? row?.FirstColon ?? '').trim(),
      secondColon: String(row?.secondColon ?? row?.SecondColon ?? '').trim(),
      factor: String(row?.factor ?? row?.Factor ?? '').trim(),
    }))
    .filter(row => row.threadType && row.firstColon && row.secondColon && row.factor);
};

const toScaledWeightInt = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;

  // Weights are already frontend-scaled by 10000. Keep integer payload for backend int fields.
  return Math.trunc(parsed);
};

function* syncWeaveFactors({ sessionUserId, weaveId, weaveFactorRowsByThreadType, allowUpdate }) {
  if (!weaveId) return;

  if (allowUpdate) {
    // Keep update behavior aligned with SampleFactor flow:
    // delete all existing rows, then create current rows from UI state.
    const existingResponse = yield call(endpoints.getWeaveFactors, {
      SessionUserId: sessionUserId,
      WeaveId: String(weaveId),
      PrdWeaveGuid: String(weaveId),
    });
    const existingFactors = existingResponse?.data?.WeaveFactorList || [];

    for (const existing of existingFactors) {
      const existingId = String(existing?.WeaveFactorId || '').trim();
      if (existingId) {
        console.log('Deleting WeaveFactor:', existingId);
        try {
          yield call(endpoints.deleteWeaveFactor, {
            SessionUserId: sessionUserId,
            WeaveFactorId: existingId,
          });
        } catch (deleteError) {
          console.error('WeaveFactor delete error:', deleteError);
        }
      }
    }
  }

  const factorRows = flattenWeaveFactorRows(weaveFactorRowsByThreadType);
  if (factorRows.length === 0) return;

  for (const row of factorRows) {
    const createPayload = {
      SessionUserId: sessionUserId,
      PrdWeaveId: String(weaveId),
      ThreadType: row.threadType,
      FirstColon: row.firstColon,
      SecondColon: row.secondColon,
      Factor: row.factor,
    };

    console.log('Creating WeaveFactor:', createPayload);
    yield call(endpoints.createWeaveFactor, createPayload);
  }
}

function* getWeaveSaga(action) {
  try {
    const sessionUserId = localStorage.getItem('sessionUserId');
    const payload = {
      SessionUserId: sessionUserId,
      ...(action.payload || {})
    };
    const { data } = yield call(endpoints.getWeaves, payload);
    yield put(actions.getWeaveDataAction(data));
  } catch (e) {
    yield put(actions.getWeaveErrorAction(e));
    yield call(errorHandler, e);
  }
}

function* createWeaveSaga(action) {
  try {
    console.log('createWeaveSaga action:', action);
    
    const {
      weaveData,
      allTableData,
      weaveFactorRowsByThreadType
    } = action.payload.data;
    
    // Convert numeric fields to strings for API
    const cleanWeaveData = {
      ...weaveData,
      WarpDensity: weaveData.WarpDensity ? weaveData.WarpDensity.toString() : "",
      WeftDensity: weaveData.WeftDensity ? weaveData.WeftDensity.toString() : "",
      ReedWidth: weaveData.ReedWidth ? weaveData.ReedWidth.toString() : "",
      ReedNo: weaveData.ReedNo ? weaveData.ReedNo.toString() : "",
      Dgts: weaveData.Dgts ? weaveData.Dgts.toString() : "",
      TotalThreads: weaveData.TotalThreads ? weaveData.TotalThreads.toString() : ""
    };
    
    console.log('Create weave API payload:', cleanWeaveData);
    
    // Step 1: Create the weave first
    const { data: weaveResponse } = yield call(endpoints.createWeave, cleanWeaveData);
    console.log('Create weave API response:', weaveResponse);
    
    yield put(actions.createWeaveDataAction(weaveResponse));
    
    if (weaveResponse.Result && weaveResponse.Result.ErrorCode === 0) {
      // Show success alert
      yield put(
        alert.setAlertAction({
          title: 'Success',
          text: weaveResponse.Result.ErrorDescription||'Weave created successfully',
          variant: 'success',
        }),
      );

      // Step 2: Use WeaveId returned directly from AddWeave response
      const weaveId =
        weaveResponse?.PrdWeaveId ||
        weaveResponse?.PrdWeaveGuid ||
        weaveResponse?.WeaveId;

      console.log('Extracted WeaveId from create response:', weaveId);

      if (weaveId && allTableData && allTableData.length > 0) {
        try {
          console.log('Processing table data:', {
            tableDataLength: allTableData.length,
            allTableData: allTableData
          });
          
          // Create weave color entries for each row and column
          const rowSortingByThreadType = {};
          for (let rowIndex = 0; rowIndex < allTableData.length; rowIndex++) {
            const row = allTableData[rowIndex];
            const threadTypeKey = String(row.threadType || '');
            rowSortingByThreadType[threadTypeKey] = (rowSortingByThreadType[threadTypeKey] || 0) + 1;
            const rowSorting = row.rowSorting || rowSortingByThreadType[threadTypeKey];
            
            if (row.colorId && row.yarnId) {
              for (let colIndex = 0; colIndex < row.values.length; colIndex++) {
                const threadCount = parseInt(row.values[colIndex]) || 0;
                
                if (threadCount > 0) {
                  const scaledWeight = toScaledWeightInt(row.weight);
                  const threadType = Number(row.threadType);
                  const weaveColorPayload = {
                    SessionUserId: weaveData.SessionUserId,
                    PrdWeavesGuid: weaveId,
                    PrdYarnGuid: row.yarnId,
                    PrdColorGuid: row.colorId,
                    Position: (colIndex + 1).toString(),
                    ThreadCount: threadCount.toString(),
                    ThreadType: row.threadType.toString(),
                    RowSorting: rowSorting.toString(),
                    ...(threadType === 1
                      ? { WeftWeight: scaledWeight }
                      : { WaftWeight: scaledWeight })
                  };
                  
                  try {
                    const weaveColorResponse = yield call(endpoints.createWeaveColor, weaveColorPayload);
                    console.log('WeaveColor creation response:', weaveColorResponse);
                    
                    if (weaveColorResponse.data?.Result?.ErrorCode !== 0) {
                      console.error('WeaveColor creation failed:', weaveColorResponse.data?.Result);
                    }
                  } catch (weaveColorSingleError) {
                    console.error('Single WeaveColor creation error:', weaveColorSingleError);
                    console.error('Error details:', weaveColorSingleError.response?.data);
                  }
                }
              }
            }
          }
          
          console.log('All WeaveColor entries processed successfully');
        } catch (weaveColorError) {
          console.error('Error creating WeaveColor entries:', weaveColorError);
          // Don't fail the entire operation, just log the error
        }
      } else if (!weaveId) {
        console.error('Could not find WeaveId in AddWeave response');
      } else if (!allTableData || allTableData.length === 0) {
        console.log('No table data provided, skipping WeaveColor creation');
      }

      try {
        yield call(syncWeaveFactors, {
          sessionUserId: weaveData.SessionUserId,
          weaveId,
          weaveFactorRowsByThreadType,
          allowUpdate: false,
        });
      } catch (weaveFactorError) {
        console.error('Error handling WeaveFactor operations:', weaveFactorError);
      }
      
      // Navigate after successful completion
      setTimeout(() => {
        if (action.payload.navigate) {
          action.payload.navigate('/weave');
        }
      }, 2000);
    }
    
  } catch (e) {
    console.error('createWeaveSaga error:', e);
    console.error('Error response:', e.response?.data);
    yield put(actions.createWeaveErrorAction(e));
    yield call(errorHandler, e);
  }
}

function* updateWeaveSaga(action) {
  try {
    console.log('updateWeaveSaga action:', action);
    
    const {
      weaveData,
      allTableData,
      weaveFactorRowsByThreadType
    } = action.payload.data;
    
    // Convert numeric fields to strings and ensure TotalThreads is string
    const cleanWeaveData = {
      ...weaveData,
      WarpDensity: weaveData.WarpDensity ? weaveData.WarpDensity.toString() : "",
      WeftDensity: weaveData.WeftDensity ? weaveData.WeftDensity.toString() : "",
      ReedWidth: weaveData.ReedWidth ? weaveData.ReedWidth.toString() : "",
      ReedNo: weaveData.ReedNo ? weaveData.ReedNo.toString() : "",
      Dgts: weaveData.Dgts ? weaveData.Dgts.toString() : "",
      TotalThreads: weaveData.TotalThreads ? weaveData.TotalThreads.toString() : ""
    };
    
    console.log('Update weave API payload:', cleanWeaveData);
    
    // Step 1: Update the weave first
    const { data: weaveResponse } = yield call(endpoints.updateWeave, cleanWeaveData);
    console.log('Update weave API response:', weaveResponse);
    
    yield put(actions.updateWeaveDataAction(weaveResponse));
    
    if (weaveResponse.Result && weaveResponse.Result.ErrorCode === 0) {
      // Show success alert
      yield put(
        alert.setAlertAction({
          title: 'Success',
          text: weaveResponse.Result.ErrorDescription||'Weave updated successfully',
          variant: 'success',
        }),
      );

      // Step 2: Handle WeaveColor updates
      const weaveId = weaveData.WeaveId;
      
      if (weaveId && allTableData && allTableData.length > 0) {
        try {
          // First, get existing WeaveColor data
          const existingWeaveColorsResponse = yield call(endpoints.getWeaveColors, {
            SessionUserId: weaveData.SessionUserId,
            PrdWeaveGuid: weaveId
          });
          
          const existingWeaveColors = existingWeaveColorsResponse.data?.WeaveColorList || [];
          console.log('Existing WeaveColors:', existingWeaveColors);
          
          // Create a map of existing weave colors for easy lookup
          const existingMap = new Map();
          existingWeaveColors.forEach(item => {
            const key = `${item.PrdColorGuid}-${item.PrdYarnGuid}-${item.Position}-${item.ThreadType}`;
            existingMap.set(key, item);
          });
          
          // Process new table data
          const newDataMap = new Map();
          const rowSortingByThreadType = {};
          for (let rowIndex = 0; rowIndex < allTableData.length; rowIndex++) {
            const row = allTableData[rowIndex];
            const threadTypeKey = String(row.threadType || '');
            rowSortingByThreadType[threadTypeKey] = (rowSortingByThreadType[threadTypeKey] || 0) + 1;
            const rowSorting = row.rowSorting || rowSortingByThreadType[threadTypeKey];
            
            if (row.colorId && row.yarnId) {
              for (let colIndex = 0; colIndex < row.values.length; colIndex++) {
                const threadCount = parseInt(row.values[colIndex]) || 0;
                const position = colIndex + 1;
                const key = `${row.colorId}-${row.yarnId}-${position}-${row.threadType}`;
                
                if (threadCount > 0) {
                  const scaledWeight = toScaledWeightInt(row.weight);
                  newDataMap.set(key, {
                    colorId: row.colorId,
                    yarnId: row.yarnId,
                    position: position,
                    threadCount: threadCount,
                    threadType: row.threadType,
                    rowSorting: rowSorting,
                    weight: scaledWeight
                  });
                }
              }
            }
          }
          
          // Update existing records and create new ones
          for (const [key, newData] of newDataMap) {
            const existingRecord = existingMap.get(key);
            
            if (existingRecord) {
              // Update existing record with weight
              const threadType = Number(newData.threadType);
              const updatePayload = {
                SessionUserId: weaveData.SessionUserId,
                WeaveColorId: existingRecord.WeaveColorId,
                PrdWeavesGuid: weaveId,
                PrdYarnGuid: newData.yarnId,
                PrdColorGuid: newData.colorId,
                Position: newData.position,
                ThreadCount: newData.threadCount,
                ThreadType: newData.threadType,
                RowSorting: newData.rowSorting.toString(),
                ...(threadType === 1
                  ? { WeftWeight: toScaledWeightInt(newData.weight) }
                  : { WaftWeight: toScaledWeightInt(newData.weight) })
              };
              
              console.log('Updating WeaveColor:', updatePayload);
              const updateResponse = yield call(endpoints.updateWeaveColor, updatePayload);
              console.log('WeaveColor update response:', updateResponse);
            } else {
              // Create new record with frontend-calculated weight.
              const threadType = Number(newData.threadType);
              const createPayload = {
                SessionUserId: weaveData.SessionUserId,
                PrdWeavesGuid: weaveId,
                PrdYarnGuid: newData.yarnId,
                PrdColorGuid: newData.colorId,
                Position: newData.position.toString(),
                ThreadCount: newData.threadCount.toString(),
                ThreadType: newData.threadType.toString(),
                RowSorting: newData.rowSorting.toString(),
                ...(threadType === 1
                  ? { WeftWeight: toScaledWeightInt(newData.weight) }
                  : { WaftWeight: toScaledWeightInt(newData.weight) })
              };
              
              console.log('Creating new WeaveColor:', createPayload);
              const createResponse = yield call(endpoints.createWeaveColor, createPayload);
              console.log('WeaveColor creation response:', createResponse);
            }
          }
          
          // Delete records that are no longer needed
          for (const [key, existingRecord] of existingMap) {
            if (!newDataMap.has(key)) {
              const deletePayload = {
                SessionUserId: weaveData.SessionUserId,
                WeaveColorId: existingRecord.WeaveColorId
              };
              
              console.log('Deleting WeaveColor:', deletePayload);
              const deleteResponse = yield call(endpoints.deleteWeaveColor, deletePayload);
              console.log('WeaveColor deletion response:', deleteResponse);
            }
          }
          
          console.log('All WeaveColor operations completed successfully');
        } catch (weaveColorError) {
          console.error('Error handling WeaveColor operations:', weaveColorError);
        }
      }

      if (weaveId) {
        try {
          yield call(syncWeaveFactors, {
            sessionUserId: weaveData.SessionUserId,
            weaveId,
            weaveFactorRowsByThreadType,
            allowUpdate: true,
          });
        } catch (weaveFactorError) {
          console.error('Error handling WeaveFactor operations:', weaveFactorError);
        }
      }
      
      // Refresh weave list
      yield put(actions.getWeaveRequestAction());
      
      // Navigate after successful completion
      if (action.payload.navigate) {
        setTimeout(() => {
          action.payload.navigate('/weave');
        }, 1000);
      }
    }
    
  } catch (e) {
    console.error('updateWeaveSaga error:', e);
    console.error('Error response:', e.response?.data);
    yield put(actions.updateWeaveErrorAction(e));
    yield call(errorHandler, e);
  }
}

function* deleteWeaveSaga(action) {
  try {
    console.log('deleteWeaveSaga action:', action);
    
    const payload = action.payload.data;
    
    console.log('Delete weave API payload:', payload);
    
    const { data } = yield call(endpoints.deleteWeave, payload);
    console.log('Delete weave API response:', data);
    
    yield put(actions.deleteWeaveDataAction(data));
    
    if (data.Result && data.Result.ErrorCode === 0) {
      // Show success alert
      yield put(
        alert.setAlertAction({
          title: 'Success',
          text: data.Result.ErrorDescription||'Weave deleted successfully',
          variant: 'success',
        }),
      );

      yield put(actions.getWeaveRequestAction());
    }
    
  } catch (e) {
    console.error('deleteWeaveSaga error:', e);
    console.error('Error response:', e.response?.data);
    yield put(actions.deleteWeaveErrorAction(e));
    yield call(errorHandler, e);
  }
}

export function* weaveSaga() {
  yield all([
    takeEvery(types.GET_WEAVE_REQUEST, getWeaveSaga),
    takeEvery(types.CREATE_WEAVE_REQUEST, createWeaveSaga),
    takeEvery(types.UPDATE_WEAVE_REQUEST, updateWeaveSaga),
    takeEvery(types.DELETE_WEAVE_REQUEST, deleteWeaveSaga),
  ]);
}
